import React from 'react';
import { DoshaPercentages } from '../../types';

interface DoshaChartProps {
  percentages: DoshaPercentages;
}

export function DoshaChart({ percentages }: DoshaChartProps) {
  const { vata, pitta, kapha } = percentages;
  
  // Calculate total for validation
  const total = vata + pitta + kapha;
  
  // Normalize percentages to ensure they add up to 100%
  const normalizedVata = Math.round((vata / total) * 100);
  const normalizedPitta = Math.round((pitta / total) * 100);
  const normalizedKapha = Math.round((kapha / total) * 100);

  return (
    <div className="space-y-6">
      {/* Modern Percentage Display */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-blue-600/20"></div>
          <div className="relative z-10">
            <div className="text-3xl font-bold mb-1">{normalizedVata}%</div>
            <div className="text-sm font-medium opacity-90">Vata</div>
            <div className="text-xs opacity-75">Air & Space</div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-300 to-blue-400"></div>
        </div>
        
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-orange-500 to-red-500 p-4 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-red-600/20"></div>
          <div className="relative z-10">
            <div className="text-3xl font-bold mb-1">{normalizedPitta}%</div>
            <div className="text-sm font-medium opacity-90">Pitta</div>
            <div className="text-xs opacity-75">Fire & Water</div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-300 to-red-400"></div>
        </div>
        
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 p-4 text-white">
          <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-600/20"></div>
          <div className="relative z-10">
            <div className="text-3xl font-bold mb-1">{normalizedKapha}%</div>
            <div className="text-sm font-medium opacity-90">Kapha</div>
            <div className="text-xs opacity-75">Earth & Water</div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-300 to-emerald-400"></div>
        </div>
      </div>

      {/* Sleek Bar Chart */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Vata</span>
          <span className="text-sm text-gray-500">{normalizedVata}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${normalizedVata}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Pitta</span>
          <span className="text-sm text-gray-500">{normalizedPitta}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${normalizedPitta}%` }}
          ></div>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Kapha</span>
          <span className="text-sm text-gray-500">{normalizedKapha}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${normalizedKapha}%` }}
          ></div>
        </div>
      </div>

      {/* Constitutional Harmony Score */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-6 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 via-pink-400/20 to-orange-400/20"></div>
        <div className="relative z-10 text-center">
          <div className="text-sm font-medium opacity-90 mb-2">Constitutional Harmony</div>
          <div className="text-4xl font-bold mb-3">
            {Math.round(100 - Math.abs(normalizedVata - 33.33) - Math.abs(normalizedPitta - 33.33) - Math.abs(normalizedKapha - 33.33))}%
          </div>
          <div className="w-20 h-1.5 bg-white/30 rounded-full mx-auto mb-3"></div>
          <p className="text-sm opacity-80">Balance indicator based on dosha distribution</p>
        </div>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
      </div>
    </div>
  );
}